/**
 * useOrderFilters — owns the FilterDef[] for an orders listing.
 *
 * Wraps the data composables (tags, categories, custom fields) and the lazy
 * custom-field fetch wiring so that consumers (e.g. `useOrdersListing`) only
 * see a single `allFilters` computed plus an `attachLazyFetch(listing)` hook.
 */
import { computed, watch, unref } from "vue";
import type { Ref, ComputedRef } from "vue";
import { useTags, useCustomFields, useCategories } from "@/composables/listing";
import type { FilterDef, ListingState } from "@/composables/listing";
import { useContext } from "@/composables/context";
import { CustomFieldsMajorTypes } from "@/data/enums/customFields";
import { buildOrderFilters } from "@/components/app/global/orders/ordersFilters";
import type { IClient } from "@upmind-automation/types";

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

export interface UseOrderFiltersOptions {
  /** When set, client-scoped filters are suppressed (single-client view). */
  clientId?: MaybeRef<IClient["id"] | null>;
}

export interface UseOrderFiltersReturn {
  allFilters: ComputedRef<FilterDef[]>;
  /**
   * Wires lazy custom-field fetching to a listing instance: fetches when the
   * filter picker opens, and when URL-restored active filters reference a
   * custom field (so the def — and pill label — exists).
   */
  attachLazyFetch: (listing: ListingState) => void;
}

export function useOrderFilters(
  options: UseOrderFiltersOptions = {}
): UseOrderFiltersReturn {
  const clientIdRef = computed<IClient["id"] | null>(
    () => (unref(options.clientId) as IClient["id"] | null) ?? null
  );

  const ctx = useContext();
  const tagsApi = useTags();
  const categoriesApi = useCategories();
  const customFieldsApi = useCustomFields();

  const customBasketFilters = computed<FilterDef[]>(() =>
    ctx.isAdmin.value
      ? customFieldsApi.filtersFor(
          CustomFieldsMajorTypes.BASKET,
          "",
          "order_custom_fields"
        )
      : []
  );

  const customClientFilters = computed<FilterDef[]>(() =>
    ctx.isAdmin.value && !clientIdRef.value
      ? customFieldsApi.filtersFor(
          CustomFieldsMajorTypes.CLIENT,
          "client",
          "client_custom_fields"
        )
      : []
  );

  async function resolveCategoryOptions() {
    await categoriesApi.fetch();
    return categoriesApi.treeOptions.value;
  }
  async function resolveTagOptions() {
    await tagsApi.fetch();
    return tagsApi.tags.value.map(t => ({ value: t.id, label: t.name }));
  }

  const allFilters = computed<FilterDef[]>(() =>
    buildOrderFilters({
      isAdmin: ctx.isAdmin.value,
      isClient: ctx.isClient.value,
      clientId: clientIdRef.value ?? null,
      resolveTagOptions,
      resolveCategoryOptions,
      customBasketFilters: customBasketFilters.value,
      customClientFilters: customClientFilters.value
    })
  );

  function ensureBasketFieldsLoaded() {
    if (!ctx.isAdmin.value) return;
    if (customFieldsApi.basketFields.value.length) return;
    customFieldsApi.fetchBasket();
  }
  function ensureClientFieldsLoaded() {
    if (!ctx.isAdmin.value || clientIdRef.value) return;
    if (customFieldsApi.clientFields.value.length) return;
    customFieldsApi.fetchClient();
  }

  function attachLazyFetch(listing: ListingState) {
    listing.onFilterPickerOpen(() => {
      ensureBasketFieldsLoaded();
      ensureClientFieldsLoaded();
    });

    // URL-restored filters that reference a custom field need their def to
    // exist so the pill label resolves. `client.custom_fields.*` triggers
    // client fields; bare `custom_fields.*` (no `client.` prefix) triggers
    // basket fields. Custom field paths can be deeper than two segments
    // (e.g. `products.custom_fields.foo`), so we match by substring.
    watch(
      () => listing.activeFilters.value,
      afs => {
        if (!ctx.isAdmin.value) return;
        for (const af of afs) {
          if (af.field.startsWith("client.custom_fields.")) {
            ensureClientFieldsLoaded();
          } else if (af.field.includes("custom_fields.")) {
            ensureBasketFieldsLoaded();
          }
        }
      },
      { immediate: true }
    );
  }

  return { allFilters, attachLazyFetch };
}
